'use es6';

import { StatusTag } from 'foundations-components/transitional/components/StatusTag';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Button } from 'foundations-components/transitional/components/Button';
import { H5 } from 'foundations-components/transitional/components/H5';
import { Small } from 'foundations-components/transitional/components/Small';
import { useEffect, useState } from 'react';
import { getSuspensionStatus } from '../api/suspensionStatus';
import reviewState from '../constants/reviewState';
import InformationModal from './InformationModal';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import usePrevious from 'react-utils/hooks/usePrevious';
import isEqual from 'hs-lodash/isEqual';
import getEventKey from '../utils/getEventKey';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { View } from 'foundations-components/transitional/components/View';
import { InfoIcon } from 'foundations-assets/icons/components/InfoIcon';
import { WarningIcon } from 'foundations-assets/icons/components/WarningIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const WarningTag = ({
  contentId,
  contentReviewData,
  withBulkData,
  keyWord,
  usageTracker,
  inline = false,
  isPublished
}) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const [contentType, setContentType] = useState('content');
  const [slaCopyType, setSlaCopyType] = useState(null);
  const prevContentId = usePrevious(contentId);
  useEffect(() => {
    let cancel = false;
    if (!withBulkData && !isEqual(prevContentId, contentId)) {
      getSuspensionStatus(contentId, keyWord).then(data => {
        if (cancel) return;
        const contentData = Object.assign({}, data, {
          contentType: keyWord
        });
        setStatus(contentData.reviewState);
        setContentType(contentData.contentType);
        setSlaCopyType(contentData.slaCopyType);
      }).catch(() => setStatus(null));
    }
    return () => {
      cancel = true;
    };
  }, [contentId, withBulkData]); // eslint-disable-line

  useEffect(() => {
    if (withBulkData && contentReviewData && contentReviewData.reviewList && contentId.toString() in contentReviewData.reviewList) {
      const reviewContent = contentReviewData.reviewList[contentId.toString()];
      setStatus(reviewContent.reviewState);
      setContentType(contentReviewData.contentType);
      setSlaCopyType(reviewContent.slaCopyType);
    }
  }, [contentReviewData, withBulkData, contentId]);
  const handleClick = e => {
    if (usageTracker) {
      usageTracker.track(getEventKey(keyWord), {
        action: 'open-close-modal-click'
      });
    }
    e.stopPropagation();
    setOpen(!open);
  };
  const determineLabelType = () => {
    if (keyWord === 'form' && isPublished) return null;
    if (status === reviewState.BLOCKED) return 'danger';
    if (status === reviewState.IN_REVIEW) return 'warning';
    return null;
  };
  const labelType = determineLabelType();
  const showLabel = !!labelType;
  useEffect(() => {
    if (showLabel && usageTracker) {
      usageTracker.track(getEventKey(keyWord), {
        action: 'warning-tag-loaded'
      });
    }
  }, [showLabel]); // eslint-disable-line

  const tooltipPlacement = withBulkData ? 'bottom' : 'bottom right';
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [open && /*#__PURE__*/_jsx(InformationModal, {
      toggleModal: handleClick,
      status: status,
      contentType: contentType,
      slaCopyType: slaCopyType,
      usageTracker: usageTracker
    }), showLabel && /*#__PURE__*/_jsx(Tooltip, {
      placement: tooltipPlacement,
      use: "longform",
      title: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(H5, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `contentLabel.${status}.toolTipHeader`
          })
        }), /*#__PURE__*/_jsx("div", {
          "data-test-id": "tooltip-message-test",
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: `contentLabel.${status}.toolTipText.${contentType}`
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "link",
          onClick: handleClick,
          "data-test-id": "tooltip-button-test",
          marginBlockStart: "300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `contentLabel.${status}.toolTipButton`
          })
        })]
      }),
      children: inline ? /*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx(View, {
          as: "span",
          marginInlineEnd: "200",
          children: /*#__PURE__*/_jsx(WarningIcon, {
            size: "200",
            alignWithText: true,
            "data-test-id": "warning-icon",
            color: labelType === 'warning' ? 'fill-caution-default' : 'fill-alert-default'
          })
        }), /*#__PURE__*/_jsx(StatusTag, {
          use: labelType,
          paddingInline: "0"
        })]
      }) : /*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx(StatusTag, {
          use: labelType,
          paddingInline: "0",
          marginBlockEnd: "0"
        }), /*#__PURE__*/_jsx(Small, {
          use: "help",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `contentLabel.${status}.labelText`
          })
        }), /*#__PURE__*/_jsx(View, {
          as: "span",
          marginInline: "100",
          children: /*#__PURE__*/_jsx(InfoIcon, {
            size: "200",
            alignWithText: true,
            "data-test-id": "info-icon",
            color: "icon-core-subtle"
          })
        })]
      })
    })]
  });
};
WarningTag.propTypes = {
  contentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  contentReviewData: PropTypes.object,
  inline: PropTypes.bool,
  keyWord: PropTypes.string.isRequired,
  withBulkData: PropTypes.bool,
  usageTracker: PropTypes.object,
  isPublished: PropTypes.bool
};
export default WarningTag;